import { Button, Div, Label, Loader, PageSlider, Slider, Touchable } from "@grassrootly/rn-core";
import { RootStackScreenProps } from "../../navigation/Navigation.types";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import React from "react";
import GameSectionVote from "./GameSectionVote";
import { useGameVoteOrganization } from "./useGameVoteOrganization";
import { VoteType } from "@grassrootly/api";
import { Platform } from "react-native";

/**
 * GameVoteOrganizationScreen - Voting interface for ORGANIZATION_MATCH games
 * 
 * Four-tab interface:
 * - Tab 1: Vote for best opponent player (overall)
 * - Tab 2: Vote for teammate with best Effort
 * - Tab 3: Vote for teammate with best Defense
 * - Tab 4: Vote for teammate with best Creativity
 * 
 * "Not sure" option available for all votes (represented as null)
 */
export default function GameVoteOrganizationScreen({ route, navigation }: RootStackScreenProps<"vote">) {
  const insents = useSafeAreaInsets();
  const { game, opponents, teammates, submitVotes, saving, goBack } = useGameVoteOrganization(
    route.params.id,
    navigation
  );

  const [tabIndex, setTabIndex] = React.useState(0);
  const pageRef = React.useRef<Slider>(null);

  // Opponent vote (Tab 1)
  const [opponentOverall, setOpponentOverall] = React.useState<number | null>(null);

  // Teammate votes (Tabs 2-4)
  const [teammateEffort, setTeammateEffort] = React.useState<number | null>(null);
  const [teammateDefense, setTeammateDefense] = React.useState<number | null>(null);
  const [teammateCreativity, setTeammateCreativity] = React.useState<number | null>(null);

  const handleSubmit = () => {
    const votes = [
      { type: VoteType.OPPONENT_OVERALL, attendanceId: opponentOverall },
      { type: VoteType.TEAMMATE_EFFORT, attendanceId: teammateEffort },
      { type: VoteType.TEAMMATE_DEFENSE, attendanceId: teammateDefense },
      { type: VoteType.TEAMMATE_CREATIVITY, attendanceId: teammateCreativity },
    ];
    submitVotes(votes);
  };

  return (
    <Div
      classNames={[
        "flex1",
        "bgNeutralDarkerDark",
        "bwTop-{1}",
        "borderPrimary",
        "p16",
        `pb-{${insents.bottom + 16 + (Platform.OS == "web" ? 16 : 0)}}`,
        "h100",
      ]}
    >
      {!game ? (
        <Loader size={"large"} />
      ) : (
        <Div classNames={["flex1", "maxW-{920}", "w-{100%}", "mr-{auto}", "ml-{auto}"]}>
          <Touchable classNames={["ml-{auto}"]} onPress={goBack}>
            <Label type="l2Regular" classNames={["colorPrimaryMain"]}>
              Later
            </Label>
          </Touchable>
          <Div classNames={["flex1", "mt-{24}"]}>
            <Label type="l1Regular" classNames={["textCenter"]}>
              The Game has finished.
            </Label>
            <Label type="l1Regular" classNames={["textCenter", "mb-{36}"]}>
              Let's vote for the best players!
            </Label>

            <PageSlider
              classNames={["flex1", "mb-{24}"]}
              initialPageIndex={tabIndex}
              onPageChange={setTabIndex}
              slider={pageRef}
              slides={[
                // Tab 1: Opponents
                <GameSectionVote
                  key="opponents"
                  title="Vote for Best Opponent Player"
                  attendees={opponents}
                  onSelected={setOpponentOverall}
                  selectedId={opponentOverall}
                  hostTeamName={game.host_team_name}
                  hostTeamLogo={game.host_team_logo}
                  visitorTeamName={game.visitor_team_name}
                  visitorTeamLogo={game.visitor_team_logo}
                />,
                // Tab 2: Teammate Effort
                <GameSectionVote
                  key="effort"
                  title="Who was the Workhorse? (Effort)"
                  attendees={teammates}
                  onSelected={setTeammateEffort}
                  selectedId={teammateEffort}
                  hostTeamName={game.host_team_name}
                  hostTeamLogo={game.host_team_logo}
                  visitorTeamName={game.visitor_team_name}
                  visitorTeamLogo={game.visitor_team_logo}
                />,
                // Tab 3: Teammate Defense
                <GameSectionVote
                  key="defense"
                  title="Who was the best Defender?"
                  attendees={teammates}
                  onSelected={setTeammateDefense}
                  selectedId={teammateDefense}
                  hostTeamName={game.host_team_name}
                  hostTeamLogo={game.host_team_logo}
                  visitorTeamName={game.visitor_team_name}
                  visitorTeamLogo={game.visitor_team_logo}
                />,
                // Tab 4: Teammate Creativity
                <GameSectionVote
                  key="creativity"
                  title="Who was the best Playmaker? (Creativity)"
                  attendees={teammates}
                  onSelected={setTeammateCreativity}
                  selectedId={teammateCreativity}
                  hostTeamName={game.host_team_name}
                  hostTeamLogo={game.host_team_logo}
                  visitorTeamName={game.visitor_team_name}
                  visitorTeamLogo={game.visitor_team_logo}
                />,
              ]}
            />

            {/* Page Indicators */}
            <Div classNames={["flexRow", "alignItemsCenter", "justifyContentCenter", "mv-{8}", "mt-{auto}", "mb-{24}"]}>
              {[0, 1, 2, 3].map((indicator, i) => (
                <Touchable
                  onPress={() => pageRef.current?.setPage(i)}
                  key={i}
                  classNames={[
                    "w-{13}",
                    "h-{3}",
                    "p-{2}",
                    "br-{3}",
                    "mh-{5}",
                    `opacity-{${tabIndex == i ? "1" : "0.2"}}`,
                    tabIndex == i ? "bgPrimaryMain" : "bgColor-{lightgray}",
                  ]}
                />
              ))}
            </Div>

            {/* Action Buttons */}
            {tabIndex === 3 ? (
              // Last tab: Show Back and Submit
              <Div classNames={["flexRow", "gap-{8}"]}>
                <Button
                  title="Back"
                  classNames={["btn", "btnSecondary", "flex1"]}
                  onPress={() => pageRef.current?.setPage(tabIndex - 1)}
                />
                <Button
                  title="Submit Votes"
                  classNames={["btn", "btnPrimary", "flex1"]}
                  isLoading={saving}
                  disabled={saving}
                  onPress={handleSubmit}
                />
              </Div>
            ) : (
              // Other tabs: Show Next button
              <Button
                title={tabIndex === 0 ? "Next: Vote for Teammates" : "Next"}
                classNames={[
                  "btn", 
                  "btnPrimary", 
                  "btnBlock",
                  ((tabIndex === 0 && !opponentOverall) || 
                   (tabIndex === 1 && !teammateEffort) || 
                   (tabIndex === 2 && !teammateDefense)) ? "opacity-{0.5}" : ""
                ]}
                onPress={() => pageRef.current?.setPage(tabIndex + 1)}
                disabled={
                  (tabIndex === 0 && !opponentOverall) || 
                  (tabIndex === 1 && !teammateEffort) || 
                  (tabIndex === 2 && !teammateDefense)
                }
              />
            )}
          </Div>
        </Div>
      )}
    </Div>
  );
}
